import React from 'react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { ClockIcon, MapPinIcon, UserIcon, CheckCircleIcon, UsersIcon } from 'lucide-react';
import { useEvent } from '../contexts/EventContext';
import { Session } from '../types';
interface SessionCardProps {
  session: Session;
  onBook: (sessionId: string) => void;
  onViewDetails: (sessionId: string) => void;
  featureName?: string;
  bookedLabel?: string;
  removeLabel?: string;
}
export function SessionCard({
  session,
  onBook,
  onViewDetails,
  featureName,
  bookedLabel = 'Scheduled',
  removeLabel = 'Remove from Schedule'
}: SessionCardProps) {
  const {
    currentEvent
  } = useEvent();
  // Feature-specific colors for Dubai Active Industry
  const getFeatureColor = (featureId: string): {
    bg: string;
    text: string;
    border: string;
  } => {
    const featureColors: Record<string, {
      bg: string;
      text: string;
      border: string;
    }> = {
      // Dubai Active Industry features
      xpro: {
        bg: '#9333ea',
        text: '#ffffff',
        border: '#9333ea'
      },
      'future-wellness': {
        bg: '#ec4899',
        text: '#ffffff',
        border: '#ec4899'
      },
      'me-fitness': {
        bg: '#000000',
        text: '#ffffff',
        border: '#000000'
      },
      'mena-roundtable': {
        bg: '#0891b2',
        text: '#ffffff',
        border: '#0891b2'
      },
      // Dubai Active Show features - UPDATED
      'active-arena': {
        bg: '#17313a',
        text: '#ffffff',
        border: '#17313a'
      },
      'main-stage': {
        bg: '#c8e1d9',
        text: '#17313a',
        border: '#c8e1d9'
      },
      'yoga-pilates': {
        bg: '#f8c8dc',
        text: '#17313a',
        border: '#f8c8dc'
      },
      'dubai-active-talks': {
        bg: '#0f4233',
        text: '#ffffff',
        border: '#0f4233'
      },
      // Dubai Muscle Show features
      'muscle-classic': {
        bg: '#ef4444',
        text: '#ffffff',
        border: '#ef4444'
      },
      'muscle-talks': {
        bg: '#dc2626',
        text: '#ffffff',
        border: '#dc2626'
      },
      fmg: {
        bg: '#f97316',
        text: '#ffffff',
        border: '#f97316'
      },
      'team-nogueira': {
        bg: '#ea580c',
        text: '#ffffff',
        border: '#ea580c'
      },
      'meet-greet': {
        bg: '#fb923c',
        text: '#ffffff',
        border: '#fb923c'
      }
    };
    return featureColors[featureId] || {
      bg: '#6b7280',
      text: '#ffffff',
      border: '#6b7280'
    };
  };
  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      Bodybuilding: 'warning',
      Fitness: 'success',
      Wellness: 'default',
      Business: 'error',
      Education: 'default'
    };
    return colors[category] || 'default';
  };
  const getTextColor = () => {
    if (currentEvent.id === 'active') {
      return '#17313a';
    }
    return 'white';
  };
  const isNearCapacity = session.capacity && session.currentAttendees ? session.currentAttendees / session.capacity > 0.8 : false;
  // Get moderators from speakers array
  const moderators = session.speakers?.filter(s => s.role === 'Moderator') || [];
  const chairs = session.speakers?.filter(s => s.role === 'Chair') || [];
  const regularSpeakers = session.speakers?.filter(s => !s.role || s.role !== 'Moderator' && s.role !== 'Chair') || [];
  // Get feature-specific colors
  const featureColors = getFeatureColor(session.feature);
  const primaryColor = currentEvent.colors.primary;
  return <Card padding="md" className="relative" data-id="element-4069">
      {session.isBooked && <div className="absolute top-3 right-3 z-10" data-id="element-4070">
          <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold font-gotham" style={{
        backgroundColor: `${currentEvent.colors.primary}20`,
        color: currentEvent.colors.primary
      }} data-id="element-4071">
            <CheckCircleIcon className="w-3 h-3" data-id="element-4072" />
            Scheduled
          </div>
        </div>}

      {/* Session Image - 1:1 Ratio */}
      {session.image && <div className="mb-3 -mx-4 -mt-4 relative" style={{
      paddingBottom: '100%'
    }} data-id="element-4073">
          <img src={session.image} alt={session.title} className="absolute inset-0 w-full h-full object-cover rounded-t-lg" data-id="element-4074" />
        </div>}

      <div className="mb-3" data-id="element-4075">
        <div className="flex items-start gap-2 mb-2" data-id="element-4076">
          {/* Feature Badge with custom color */}
          <div className="px-2 py-1 rounded-md text-xs font-semibold font-gotham" style={{
          backgroundColor: featureColors.bg,
          color: featureColors.text
        }} data-id="element-4077">
            {featureName || session.category}
          </div>
          {session.capacity && <Badge variant={isNearCapacity ? 'warning' : 'default'} size="sm" data-id="element-4078">
              <UsersIcon className="w-3 h-3 mr-1 inline" data-id="element-4079" />
              {session.currentAttendees}/{session.capacity}
            </Badge>}
        </div>

        <h3 className="font-bold text-gray-900 font-gotham mb-1 pr-16" data-id="element-4080">
          {session.title}
        </h3>

        {session.speaker && !session.speakers && <div className="flex items-center gap-1 text-sm text-gray-600 font-gotham mb-2" data-id="element-4081">
            <UserIcon className="w-4 h-4" data-id="element-4082" />
            {session.speaker}
          </div>}

        <p className="text-sm text-gray-600 font-gotham line-clamp-2 mb-3" data-id="element-4083">
          {session.description}
        </p>
      </div>

      {/* Speakers Section */}
      {regularSpeakers.length > 0 && <div className="mb-3 pb-3 border-b border-gray-100" data-id="element-4084">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 font-gotham" data-id="element-4085">
            Speakers
          </p>
          <div className="space-y-2" data-id="element-4086">
            {regularSpeakers.map((speaker, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-4087">
                <img src={speaker.image} alt={speaker.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" data-id="element-4088" />
                <div className="flex-1 min-w-0" data-id="element-4089">
                  <div className="font-semibold text-sm text-gray-900 font-gotham" data-id="element-4090">
                    {speaker.name}
                  </div>
                  <div className="text-xs text-gray-600 font-gotham" data-id="element-4091">
                    {speaker.title}
                  </div>
                </div>
              </div>)}
          </div>
        </div>}

      {/* Moderated By Section */}
      {moderators.length > 0 && <div className="mb-3 pb-3 border-b border-gray-100" data-id="element-4092">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 font-gotham" data-id="element-4093">
            Moderated By
          </p>
          <div className="space-y-2" data-id="element-4094">
            {moderators.map((moderator, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-4095">
                <img src={moderator.image} alt={moderator.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" data-id="element-4096" />
                <div className="flex-1 min-w-0" data-id="element-4097">
                  <div className="font-semibold text-sm text-gray-900 font-gotham" data-id="element-4098">
                    {moderator.name}
                  </div>
                  <div className="text-xs text-gray-600 font-gotham" data-id="element-4099">
                    {moderator.title}
                  </div>
                </div>
              </div>)}
          </div>
        </div>}

      {/* Chaired By Section */}
      {chairs.length > 0 && <div className="mb-3 pb-3 border-b border-gray-100" data-id="element-4100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 font-gotham" data-id="element-4101">
            Chaired By
          </p>
          <div className="space-y-2" data-id="element-4102">
            {chairs.map((chair, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-4103">
                <img src={chair.image} alt={chair.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" data-id="element-4104" />
                <div className="flex-1 min-w-0" data-id="element-4105">
                  <div className="font-semibold text-sm text-gray-900 font-gotham" data-id="element-4106">
                    {chair.name}
                  </div>
                  <div className="text-xs text-gray-600 font-gotham" data-id="element-4107">
                    {chair.title}
                  </div>
                </div>
              </div>)}
          </div>
        </div>}

      <div className="flex items-center gap-4 text-sm text-gray-600 font-gotham mb-3" data-id="element-4108">
        <div className="flex items-center gap-1" data-id="element-4109">
          <ClockIcon className="w-4 h-4" data-id="element-4110" />
          {session.startTime} - {session.endTime}
        </div>
        <div className="flex items-center gap-1" data-id="element-4111">
          <MapPinIcon className="w-4 h-4" data-id="element-4112" />
          {session.location}
        </div>
      </div>

      <div className="flex gap-2" data-id="element-4113">
        <button onClick={() => onViewDetails(session.id)} className="flex-1 px-3 py-2 rounded-lg text-sm font-medium font-gotham border-2 transition-all hover:bg-opacity-10" style={{
        borderColor: currentEvent.colors.primary,
        color: currentEvent.colors.primary
      }} data-id="element-4114">
          View Details
        </button>
        <button onClick={e => {
        e.stopPropagation();
        onBook(session.id);
      }} className="flex-1 px-4 py-2 rounded-lg text-sm font-medium font-gotham transition-all" style={session.isBooked ? {
        backgroundColor: '#f3f4f6',
        color: '#374151'
      } : {
        backgroundColor: primaryColor,
        color: 'white'
      }} data-id="element-4115">
          {session.isBooked ? removeLabel : 'Add to Schedule'}
        </button>
      </div>
    </Card>;
}